import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

const SLOT_ATC_CLASS = 'slot-atc';
const SLOT_ATC_ANIMATING_CLASS = 'slot-atc--animating';
const SLOT_ATC_NO_TRANSITION_CLASS = 'slot-atc--no-transition';
const SLOT_ATC_LAST_VARIANT_ANIM_AT_KEY = 'slotAtcLastVariantAnimAt';

/**
 * @param {HTMLElement | null | undefined} el
 */
function isSlotAtcButton(el) {
  return !!el && el instanceof HTMLButtonElement && el.classList.contains(SLOT_ATC_CLASS);
}

/**
 * @param {HTMLButtonElement} button
 */
function getSlotAtcParts(button) {
  return {
    mask: button.querySelector('[data-slot-atc-mask]'),
    track: button.querySelector('[data-slot-atc-track]'),
    itemA: button.querySelector('[data-slot-atc-a]'),
    itemB: button.querySelector('[data-slot-atc-b]'),
    textA: button.querySelector('[data-slot-atc-a] .add-to-cart-text__content'),
    textB: button.querySelector('[data-slot-atc-b] .add-to-cart-text__content'),
    srText: button.querySelector('[data-slot-atc-sr]'),
    addedText: button.querySelector('[data-slot-atc-added]'),
  };
}

/**
 * Updates slot ATC text immediately (no animation).
 * @param {HTMLButtonElement} button
 * @param {string} text
 */
function setSlotAtcTextImmediate(button, text) {
  const { textA, textB, srText } = getSlotAtcParts(button);
  if (textA) textA.textContent = text;
  if (textB) textB.textContent = text;
  if (srText) srText.textContent = text;
}

/**
 * Triggers the slot-machine animation to a target label.
 * Always replays, even if the text is unchanged.
 * Does nothing (and updates immediately) if the button is disabled.
 *
 * @param {HTMLButtonElement} button
 * @param {string} targetText
 */
function animateSlotAtcTo(button, targetText) {
  if (!isSlotAtcButton(button)) return;

  // Disable animation when button is disabled
  if (button.disabled) {
    setSlotAtcTextImmediate(button, targetText);
    return;
  }

  const { textB, track } = getSlotAtcParts(button);
  if (!textB || !track) {
    // If markup is missing, fail softly.
    return;
  }

  // Tokenize to safely cancel stale transitionend handlers
  const token = String((Number(button.dataset.slotAtcToken || '0') || 0) + 1);
  button.dataset.slotAtcToken = token;

  // Prepare "next" row label (row B)
  textB.textContent = targetText;
  const { srText } = getSlotAtcParts(button);
  if (srText) srText.textContent = targetText;

  // Force replay: reset to base state without transition, then run the transition
  button.classList.remove(SLOT_ATC_ANIMATING_CLASS);
  button.classList.add(SLOT_ATC_NO_TRANSITION_CLASS);
  // Force reflow
  void button.offsetHeight;
  button.classList.remove(SLOT_ATC_NO_TRANSITION_CLASS);
  void button.offsetHeight;
  button.classList.add(SLOT_ATC_ANIMATING_CLASS);

  const onEnd = (event) => {
    if (button.dataset.slotAtcToken !== token) return;
    if (event.target !== track || event.propertyName !== 'transform') return;

    // Snap back to base state without a reverse animation, and commit the new label.
    button.classList.add(SLOT_ATC_NO_TRANSITION_CLASS);
    button.classList.remove(SLOT_ATC_ANIMATING_CLASS);
    setSlotAtcTextImmediate(button, targetText);
    void button.offsetHeight;
    button.classList.remove(SLOT_ATC_NO_TRANSITION_CLASS);

    const pending = (button.dataset.slotAtcPendingLabel || '').trim();
    if (pending && pending !== targetText) {
      button.dataset.slotAtcPendingLabel = '';
      requestAnimationFrame(() => animateSlotAtcTo(button, pending));
    }
  };

  track.addEventListener('transitionend', onEnd, { once: true });
}

/**
 * Runs the same slot "Added" cycle as the main product ATC button.
 *
 * @param {HTMLButtonElement} button
 * @param {{ animationEnabled?: boolean }} [options]
 * @returns {{ clear: () => void }}
 */
export function animateSlotAtcAddedCycle(button, options = {}) {
  const animationEnabled = options.animationEnabled !== false;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let animationTimeout;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let cleanupTimeout;

  const clear = () => {
    if (animationTimeout) clearTimeout(animationTimeout);
    if (cleanupTimeout) clearTimeout(cleanupTimeout);
    animationTimeout = undefined;
    cleanupTimeout = undefined;
  };

  if (!animationEnabled || !isSlotAtcButton(button) || button.disabled) {
    return { clear };
  }

  const { addedText, srText } = getSlotAtcParts(button);
  const addedLabel = (addedText?.textContent || '').trim() || Theme?.translations?.added || 'Added';
  const baseLabel =
    (button.dataset.slotAtcBaseLabel || '').trim() ||
    (srText?.textContent || '').trim() ||
    Theme?.translations?.add_to_cart ||
    'Add to cart';

  button.dataset.slotAtcBaseLabel = baseLabel;

  animateSlotAtcTo(button, addedLabel);

  animationTimeout = setTimeout(() => {
    cleanupTimeout = setTimeout(() => {
      animateSlotAtcTo(button, baseLabel);
    }, 10);
  }, ADD_TO_CART_TEXT_ANIMATION_DURATION);

  return { clear };
}

export { animateSlotAtcTo };

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    const isPuppetClick = this.refs.addToCartButton?.dataset.puppet === 'true';
    if (!isPuppetClick) {
      this.animateAddToCart();

      const animationEnabled = this.dataset.addToCartAnimation === 'true';

      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /** @type {{ clear: () => void }} */
  #slotAtcCycle = { clear: () => {} };

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (isSlotAtcButton(addToCartButton)) {
      this.#slotAtcCycle.clear();
      this.#slotAtcCycle = animateSlotAtcAddedCycle(addToCartButton);
      return;
    }

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button label (and animate for main product ATC).
    if (newAddToCartButton) {
      if (isSlotAtcButton(currentAddToCartButton)) {
        const nextLabel =
          (newAddToCartButton.querySelector('[data-slot-atc-sr]')?.textContent || '').trim() ||
          (newAddToCartButton.querySelector('.add-to-cart-text__content')?.textContent || '').trim();

        if (nextLabel) {
          currentAddToCartButton.dataset.slotAtcBaseLabel = nextLabel;

          // Never mutate slot text mid-animation (avoids "repeating" frames)
          if (currentAddToCartButton.classList.contains(SLOT_ATC_ANIMATING_CLASS)) {
            currentAddToCartButton.dataset.slotAtcPendingLabel = nextLabel;
            return;
          }

          // If we already animated instantly on variant:selected, don't replay a delayed animation
          // unless the label actually changed (e.g. sold out / pre-order).
          const lastInstantAt = Number(currentAddToCartButton.dataset[SLOT_ATC_LAST_VARIANT_ANIM_AT_KEY] || '0') || 0;
          const isRecent = Date.now() - lastInstantAt < 2000;
          const currentSr = (currentAddToCartButton.querySelector('[data-slot-atc-sr]')?.textContent || '').trim();

          if (isRecent && currentSr && currentSr === nextLabel) {
            setSlotAtcTextImmediate(currentAddToCartButton, nextLabel);
          } else {
            animateSlotAtcTo(currentAddToCartButton, nextLabel);
          }
        }
      } else {
        morph(currentAddToCartButton, newAddToCartButton);
      }
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  /**
   * Handle variant selection - no longer disables the button to avoid dimming effect.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = (event) => {
    const button = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    if (!isSlotAtcButton(button)) return;

    const soldOutLabel =
      (button.querySelector('[data-slot-atc-sold-out]')?.textContent || '').trim() ||
      Theme?.translations?.sold_out ||
      'Sold out';
    const addToCartLabel =
      (button.querySelector('[data-slot-atc-add-to-cart]')?.textContent || '').trim() ||
      Theme?.translations?.add_to_cart ||
      'Add to cart';
    const unavailableLabel =
      (button.querySelector('[data-slot-atc-unavailable]')?.textContent || '').trim() || 'Unavailable';

    const isAvailable = event?.detail?.resource?.available;
    const selectedVariantId = (event?.detail?.resource?.variantId || '').trim();

    if (isAvailable === false) {
      const targetLabel = selectedVariantId ? soldOutLabel : unavailableLabel;

      // Animate immediately to Sold out/Unavailable, then dim/disable in the same tick.
      button.dataset.slotAtcBaseLabel = targetLabel;
      button.dataset[SLOT_ATC_LAST_VARIANT_ANIM_AT_KEY] = String(Date.now());
      animateSlotAtcTo(button, targetLabel);

      this.refs.addToCartButtonContainer?.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      return;
    }

    if (isAvailable === true) {
      // Undim/enable and animate directly from Sold out -> Add to cart (no intermediate frame).
      this.refs.addToCartButtonContainer?.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');

      button.dataset.slotAtcBaseLabel = addToCartLabel;
      button.dataset[SLOT_ATC_LAST_VARIANT_ANIM_AT_KEY] = String(Date.now());
      animateSlotAtcTo(button, addToCartLabel);
      return;
    }

    // Fallback: still animate instantly on variant change (even if label is unchanged)
    const baseLabel = (button.dataset.slotAtcBaseLabel || '').trim() || (button.querySelector('[data-slot-atc-sr]')?.textContent || '').trim();
    if (baseLabel) {
      button.dataset[SLOT_ATC_LAST_VARIANT_ANIM_AT_KEY] = String(Date.now());
      animateSlotAtcTo(button, baseLabel);
    }
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
